import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ExcelGrid, exportToCsv, type ExcelColumnDef } from '@/components/excelGrid'
import {
  getCombinedStockOverview,
  updateStockPoliciesBatch,
  recordStockAdjustmentBatch,
} from '@/services/inventory/apiClient'
import type { MaterialStockOverviewItem } from '@/services/inventory/types'
import {
  fetchReservationsApi,
  releaseReservationApi,
  createManualReservationApi,
  type ReservationItem,
} from '@/services/warehouse/reservationsApi'
import { StockMobileCardList } from './StockMobileCardList'
import { MATERIAL_GROUPS, getMaterialGroup } from '@/domain/materialGroups'
import { MaterialPropertiesPanel } from '@/features/materials/MaterialPropertiesPanel'
import { CreateMaterialModal } from '@/components/materials/CreateMaterialModal'
import { archiveMaterialApi, deleteMaterialApi, patchMaterialApi, restoreMaterialApi } from '@/services/materials'
import { deleteFreeStockItem } from '@/services/warehouse/freeStockApi'
import { CreateIssueSlipModal } from '@/features/warehouse/issueSlips/CreateIssueSlipModal'
import { useEscapeClose } from '@/hooks/useEscapeClose'
import { useAuth } from '@/features/auth/AuthContext'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { formatFreeStockDetails, getFreeStockItem, type FreeStockItem } from '@/services/warehouse/freeStockApi'
import { FreeConversionModal } from './freeStock/FreeConversionModal'
import { FreeStockDetailModal } from './freeStock/FreeStockDetailModal'

/** Tên gốc của vật tư (vật tư theo quy cách giữ tên gốc trong thuộc tính base_name). */
function stockBaseName(m: MaterialStockOverviewItem): string {
  const base = m.attributes && typeof (m.attributes as Record<string, unknown>).base_name === 'string' ? String((m.attributes as Record<string, unknown>).base_name) : ''
  return base || m.name || ''
}

/** Tên hiển thị một ô: tên vật tư (đã gồm quy cách, màu), kèm thông số khác nếu có; hàng tự do kèm chi tiết. */
function stockDisplayName(m: MaterialStockOverviewItem): string {
  const extra = m.is_free_stock ? formatFreeStockDetails(m.free_stock_details) : specSummaryOf(m.attributes)
  const name = m.name ?? ''
  return extra && !name.toLocaleLowerCase('vi').includes(extra.toLocaleLowerCase('vi')) ? `${name} - ${extra}` : name
}

/** Thuộc tính vật tư hiển thị ở cột Quy cách / thông số (bỏ các khóa kỹ thuật của vật tư theo quy cách: tên đã ghi đủ trong ô Tên vật tư). */
const HIDDEN_ATTRIBUTE_KEYS = new Set(['base_material_id', 'base_name', 'spec_label', 'color_label'])
function specSummaryOf(attributes: Record<string, unknown> | null | undefined): string {
  if (!attributes) return ''
  return Object.entries(attributes)
    .filter(([key, value]) => !HIDDEN_ATTRIBUTE_KEYS.has(key) && Boolean(value))
    .map(([, value]) => String(value))
    .join(' · ')
}

export function StockPage() {
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const canEdit = user?.roles.some((r) => r === 'WAREHOUSE' || r === 'ADMIN') ?? false

  const [materials, setMaterials] = useState<MaterialStockOverviewItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modals & Drawers
  const [createMaterialModalOpen, setCreateMaterialModalOpen] = useState(false)

  // Filters & State
  const [selectedGroup, setSelectedGroup] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [clearFilterCounter, setClearFilterCounter] = useState(0)
  const [resetWidthsCounter, setResetWidthsCounter] = useState(0)
  const [stockKind, setStockKind] = useState<'all' | 'material' | 'free'>(searchParams.get('kind') === 'free' ? 'free' : 'all')
  const [contextMenu, setContextMenu] = useState<{ row: MaterialStockOverviewItem; x: number; y: number } | null>(null)
  const [freeDetail, setFreeDetail] = useState<any>(null)
  const [freeConvert, setFreeConvert] = useState<FreeStockItem | null>(null)
  const [deleteMaterial, setDeleteMaterial] = useState<MaterialStockOverviewItem | null>(null)
  const [deleteFree, setDeleteFree] = useState<MaterialStockOverviewItem | null>(null)
  const [issueOpen, setIssueOpen] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteSubmitting, setDeleteSubmitting] = useState(false)
  useEscapeClose({ enabled: Boolean(deleteMaterial), hasUnsavedChanges: false, onClose: () => { setDeleteMaterial(null); setDeleteError(null) } })
  useEscapeClose({ enabled: Boolean(deleteFree), hasUnsavedChanges: false, onClose: () => { setDeleteFree(null); setDeleteError(null) } })

  // Modes: null | 'edit' | 'count'
  const [mode, setMode] = useState<'edit' | 'count' | null>(null)
  const [editSnapshot, setEditSnapshot] = useState<MaterialStockOverviewItem[] | null>(null)
  const [countedQuantities, setCountedQuantities] = useState<Record<string, number>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Modals
  const [activeHoldMaterial, setActiveHoldMaterial] = useState<MaterialStockOverviewItem | null>(null)
  const [quickHoldQty, setQuickHoldQty] = useState<string>('')
  const [quickHoldNote, setQuickHoldNote] = useState<string>('')
  const [quickHoldSubmitting, setQuickHoldSubmitting] = useState(false)
  const [activeHoldReservations, setActiveHoldReservations] = useState<ReservationItem[]>([])
  const [holdModalLoading, setHoldModalLoading] = useState(false)
  const [selectedMaterialForPanel, setSelectedMaterialForPanel] = useState<string | null>(null)

  // Stocktake confirmation modal
  const [showStocktakeModal, setShowStocktakeModal] = useState(false)
  const [stocktakeReason, setStocktakeReason] = useState('')

  // Load data
  const loadStockData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getCombinedStockOverview({ archived: showArchived })
      setMaterials(data)
    } catch (err: any) {
      setError(err.message || 'Lỗi tải dữ liệu tồn kho')
    } finally {
      setLoading(false)
    }
  }, [showArchived])

  useEffect(() => {
    void loadStockData()
  }, [loadStockData])

  // Count items by group
  const groupCounts = useMemo<Record<string, number>>(() => {
    const materialRows = materials.filter((m) => !m.is_free_stock)
    const counts: Record<string, number> = { all: materials.length, material: materialRows.length, free: materials.length - materialRows.length }
    for (const g of MATERIAL_GROUPS) {
      counts[g.code] = 0
    }
    let unassigned = 0
    for (const m of materialRows) {
      if (m.group_code && counts[m.group_code] !== undefined) {
        counts[m.group_code]++
      } else {
        unassigned++
      }
    }
    return { ...counts, unassigned }
  }, [materials])

  // Health assessment helper
  const getHealth = useCallback((m: MaterialStockOverviewItem) => {
    const avail = (m.physical_available ?? m.available) - (m.reserved_base ?? 0)
    const threshold = m.threshold ?? Math.max(m.min_qty ?? 0, m.safety_stock ?? 0)

    if (!threshold || threshold <= 0) {
      return { key: 'nothr', text: 'Không theo dõi', cls: 'text-slate-500' }
    }
    if (avail <= 0) {
      return { key: 'out', text: 'Hết khả dụng', cls: 'text-red-700 font-bold' }
    }
    if (avail <= threshold) {
      return { key: 'below', text: 'Dưới ngưỡng', cls: 'text-amber-700 font-bold' }
    }
    return { key: 'ok', text: 'Đủ tồn', cls: 'text-emerald-700 font-medium' }
  }, [])

  // Summary counts
  const summaryCounts = useMemo(() => {
    let below = 0
    let out = 0
    let untracked = 0
    for (const m of materials) {
      const h = getHealth(m)
      if (h.key === 'below') below++
      else if (h.key === 'out') out++
      else if (h.key === 'nothr') untracked++
    }
    return { below, out, untracked }
  }, [materials, getHealth])

  // Rows filtered by tab and status dropdown
  const filteredRows = useMemo(() => {
    let list = materials.filter((m) => stockKind === 'all' || (stockKind === 'free' ? m.is_free_stock : !m.is_free_stock))
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      list = list.filter((m) => `${m.code} ${m.name} ${formatFreeStockDetails(m.free_stock_details)}`.toLowerCase().includes(q))
    }

    // Group filter
    if (selectedGroup !== 'all' && stockKind !== 'free') {
      if (selectedGroup === 'unassigned') {
        list = list.filter((m) => !m.group_code)
      } else {
        list = list.filter((m) => m.group_code === selectedGroup)
      }
    }

    // Status filter
    if (statusFilter && stockKind !== 'free') {
      list = list.filter((m) => getHealth(m).key === statusFilter)
    }

    return list
  }, [materials, selectedGroup, statusFilter, searchQuery, stockKind, getHealth])

  // Open "Đang giữ cho việc gì" modal
  const handleOpenHoldModal = async (m: MaterialStockOverviewItem) => {
    setActiveHoldMaterial(m)
    setQuickHoldQty('')
    setQuickHoldNote('')
    setHoldModalLoading(true)
    try {
      const res = await fetchReservationsApi({ materialId: m.material_id, status: 'ACTIVE' })
      setActiveHoldReservations(res.data || res.items || [])
    } catch (err: any) {
      alert(err.message || 'Lỗi tải chi tiết giữ hàng')
    } finally {
      setHoldModalLoading(false)
    }
  }

  // Quick hold submission
  const handleCreateQuickHold = async () => {
    if (!activeHoldMaterial) return
    const q = Number(quickHoldQty)
    if (!Number.isFinite(q) || q <= 0) {
      alert('Số lượng giữ phải lớn hơn 0')
      return
    }
    const maxAvail = Math.max(
      0,
      (activeHoldMaterial.physical_available ?? activeHoldMaterial.available) -
        (activeHoldMaterial.reserved_base ?? 0),
    )
    if (q > maxAvail) {
      alert(
        `Không thể giữ quá số lượng có thể dùng (${maxAvail.toLocaleString('vi-VN')} ${activeHoldMaterial.uom_default || ''})`,
      )
      return
    }

    try {
      setQuickHoldSubmitting(true)
      await createManualReservationApi({
        material_id: activeHoldMaterial.material_id,
        qty_base: q,
        note: quickHoldNote.trim() || undefined,
      })
      setQuickHoldQty('')
      setQuickHoldNote('')
      // Refresh reservations
      const res = await fetchReservationsApi({
        materialId: activeHoldMaterial.material_id,
        status: 'ACTIVE',
      })
      setActiveHoldReservations(res.data || res.items || [])
      // Refresh stock data
      await loadStockData()
    } catch (err: any) {
      alert(err.message || 'Lỗi khi tạo giữ hàng')
    } finally {
      setQuickHoldSubmitting(false)
    }
  }

  const handleArchiveMaterial = async (row: MaterialStockOverviewItem) => {
    setArchiveError(null)
    try {
      await archiveMaterialApi(row.material_id)
      await loadStockData()
    } catch (err: any) {
      setArchiveError(err.message || 'Không thể lưu trữ vật tư')
    }
  }

  const handleRestoreMaterial = async (row: MaterialStockOverviewItem) => {
    setArchiveError(null)
    try {
      await restoreMaterialApi(row.material_id)
      await loadStockData()
    } catch (err: any) {
      setArchiveError(err.message || 'Không thể khôi phục vật tư')
    }
  }

  const handleDeleteFree = async () => {
    if (!deleteFree?.free_stock_id) return
    setDeleteSubmitting(true)
    setDeleteError(null)
    try {
      await deleteFreeStockItem(deleteFree.free_stock_id)
      setDeleteFree(null)
      await loadStockData()
    } catch (err: any) {
      setDeleteError(err.message || 'Không thể xóa hàng tự do')
    } finally {
      setDeleteSubmitting(false)
    }
  }

  const handleDeleteMaterial = async () => {
    if (!deleteMaterial) return
    setDeleteSubmitting(true)
    setDeleteError(null)
    try {
      await deleteMaterialApi(deleteMaterial.material_id)
      setDeleteMaterial(null)
      await loadStockData()
    } catch (err: any) {
      setDeleteError(err.message || 'Không thể xóa vật tư')
    } finally {
      setDeleteSubmitting(false)
    }
  }

  // Release reservation in modal
  const handleReleaseReservation = async (reservation: ReservationItem) => {
    const reason = prompt(
      `Lý do bỏ giữ ${reservation.remaining} ${reservation.base_uom} (lệnh ${reservation.plan_line_code || reservation.plan_line_id || 'thủ công'}) — bắt buộc:`,
    )
    if (reason === null) return
    if (!reason.trim()) {
      alert('Bắt buộc nhập lý do bỏ giữ')
      return
    }

    try {
      await releaseReservationApi(reservation.id, reason.trim())
      // Refresh reservations modal
      if (activeHoldMaterial) {
        const res = await fetchReservationsApi({ materialId: activeHoldMaterial.material_id, status: 'ACTIVE' })
        setActiveHoldReservations(res.data || res.items || [])
      }
      // Refresh stock data
      void loadStockData()
    } catch (err: any) {
      alert(err.message || 'Lỗi bỏ giữ hàng')
    }
  }

  // Start edit mode
  const handleStartEdit = () => {
    setMode('edit')
    setEditSnapshot(JSON.parse(JSON.stringify(materials)))
  }

  // Start count mode
  const handleStartCount = () => {
    setMode('count')
    setEditSnapshot(JSON.parse(JSON.stringify(materials)))
    setCountedQuantities({})
  }

  // Cancel edit/count mode
  const handleCancelMode = () => {
    if (editSnapshot) {
      setMaterials(editSnapshot)
    }
    setMode(null)
    setEditSnapshot(null)
    setCountedQuantities({})
  }

  // Confirm edit mode (batch policies & attributes)
  const handleConfirmEdit = async () => {
    if (!editSnapshot) return
    const policyChanges: Array<{ material_id: string; min_qty?: number | null; note?: string }> = []
    const attrChanges: Array<{ material_id: string; attributes: Record<string, any> }> = []

    for (let i = 0; i < materials.length; i++) {
      const cur = materials[i]
      const old = editSnapshot[i]
      if (cur.threshold !== old.threshold || cur.notes !== old.notes) {
        policyChanges.push({
          material_id: cur.material_id,
          min_qty: cur.threshold != null ? cur.threshold : null,
          note: cur.notes ?? '',
        })
      }
      if (JSON.stringify(cur.attributes ?? {}) !== JSON.stringify(old.attributes ?? {})) {
        attrChanges.push({
          material_id: cur.material_id,
          attributes: cur.attributes ?? {},
        })
      }
    }

    if (policyChanges.length === 0 && attrChanges.length === 0) {
      setMode(null)
      setEditSnapshot(null)
      return
    }

    try {
      setIsSubmitting(true)
      const promises: Promise<any>[] = []
      if (policyChanges.length > 0) {
        promises.push(updateStockPoliciesBatch(policyChanges))
      }
      for (const change of attrChanges) {
        promises.push(patchMaterialApi(change.material_id, { attributes: change.attributes }))
      }
      await Promise.all(promises)
      setMode(null)
      setEditSnapshot(null)
      await loadStockData()
    } catch (err: any) {
      alert(err.message || 'Lỗi cập nhật thông tin vật tư')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Confirm count mode -> open summary dialog
  const handlePrepareStocktake = () => {
    const diffs = Object.keys(countedQuantities).filter((id) => {
      const m = materials.find((mat) => mat.material_id === id)
      return m && countedQuantities[id] !== (m.physical_available ?? m.available)
    })

    if (diffs.length === 0) {
      alert('Chưa nhập số lượng kiểm kê chênh lệch so với tồn hệ thống')
      return
    }

    setShowStocktakeModal(true)
  }

  // Submit batch stocktake
  const handleSubmitStocktake = async (forceBelowReserved = false) => {
    if (!stocktakeReason.trim()) {
      alert('Bắt buộc nhập lý do điều chỉnh')
      return
    }

    const items = Object.keys(countedQuantities)
      .map((material_id) => ({
        material_id,
        counted_qty: countedQuantities[material_id],
      }))
      .filter((item) => {
        const m = materials.find((mat) => mat.material_id === item.material_id)
        return m && item.counted_qty !== (m.physical_available ?? m.available)
      })

    try {
      setIsSubmitting(true)
      const res = await recordStockAdjustmentBatch({
        reason: stocktakeReason.trim(),
        items,
        force_below_reserved: forceBelowReserved,
      })

      alert(`Đã tạo phiếu kiểm kê ${res.batch_code} điều chỉnh ${res.adjusted_count} vật tư`)
      setShowStocktakeModal(false)
      setStocktakeReason('')
      setMode(null)
      setEditSnapshot(null)
      setCountedQuantities({})
      await loadStockData()
    } catch (err: any) {
      if (err.code === 'ADJ_BELOW_RESERVED') {
        if (confirm(`${err.message}\n\nBạn có muốn BẮT BUỘC điều chỉnh không?`)) {
          await handleSubmitStocktake(true)
          return
        }
      } else {
        alert(err.message || 'Lỗi tạo phiếu kiểm kê')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Columns definition based on selectedGroup
  const gridColumns = useMemo<ExcelColumnDef<MaterialStockOverviewItem>[]>(() => {
    const cols: ExcelColumnDef<MaterialStockOverviewItem>[] = []

    // Không có cột Nhóm: nhóm xem ở các tab phía trên

    // 2. Tên vật tư (tên gốc, ví dụ "Ray trượt bi") và Tên chi tiết (tên - quy cách - màu, ví dụ "Ray trượt bi - 13x35x200 - trắng")
    cols.push({
      k: 'name',
      h: 'Tên vật tư',
      w: 220,
      get: (m) => stockBaseName(m),
    })
    cols.push({
      k: 'detail_name',
      h: 'Tên chi tiết',
      w: 380,
      link: true,
      get: (m) => stockDisplayName(m),
      tip: () => 'Bấm để xem/sửa thông số và thuộc tính',
    })

    // 3. Thông số: tab Tất cả không có cột riêng (mọi thông số ghi trong ô Tên vật tư); tab nhóm hiện cột đặc thù của nhóm
    if (selectedGroup === 'all') {
      // (không thêm cột)
    } else {
      const grp = getMaterialGroup(selectedGroup)
      if (grp) {
        for (const attr of grp.attributes) {
          cols.push({
            k: `attr_${attr.key}`,
            h: attr.label,
            w: attr.width || 110,
            tip: (m) => String(m.attributes?.[attr.key] ?? '') || undefined,
            num: Boolean(attr.numeric),
            edit: () => mode === 'edit',
            get: (m) => (m.attributes ? m.attributes[attr.key] ?? '' : ''),
            set: (m, val) => {
              const strVal = String(val ?? '').trim()
              if (!m.attributes) m.attributes = {}
              if (attr.numeric) {
                if (strVal === '') {
                  delete m.attributes[attr.key]
                  return
                }
                const n = Number(strVal.replace(',', '.'))
                if (!Number.isFinite(n) || n < 0) {
                  return `${attr.label} phải là số không âm hoặc để trống`
                }
                m.attributes[attr.key] = n
              } else {
                if (strVal === '') {
                  delete m.attributes[attr.key]
                } else {
                  m.attributes[attr.key] = strVal
                }
              }
            },
          })
        }
      }
    }

    // 4. ĐVT
    cols.push({
      k: 'uom_default',
      h: 'ĐVT',
      w: 60,
      ctr: true,
      get: (m) => m.uom_default || '',
    })

    // 5. Tồn thực tế
    cols.push({
      k: 'physical_available',
      h: 'Tồn thực tế',
      w: 95,
      num: true,
      get: (m) => m.physical_available ?? m.available,
    })

    // 7. Đã giữ
    cols.push({
      k: 'reserved_base',
      h: 'Đã giữ',
      w: 85,
      num: true,
      link: true,
      get: (m) => m.reserved_base ?? 0,
      tip: (m) => ((m.reserved_base ?? 0) > 0 ? 'Bấm để xem đang giữ cho việc gì' : ''),
      cls: (m) => ((m.reserved_base ?? 0) > 0 ? 'text-[#0b4fb3] font-semibold' : 'text-slate-400'),
    })

    // 8. Có thể dùng
    cols.push({
      k: 'available_to_reserve',
      h: 'Có thể dùng',
      w: 95,
      num: true,
      get: (m) => {
        const physical = m.physical_available ?? m.available
        const reserved = m.reserved_base ?? 0
        return Math.max(0, Math.round((physical - reserved) * 10000) / 10000)
      },
      cls: () => 'font-bold',
    })

    // 9. Đang về
    cols.push({
      k: 'incoming_qty',
      h: 'Đang về',
      w: 80,
      num: true,
      get: (m) => m.incoming_qty ?? m.incoming_base ?? 0,
    })

    // 10. Dự kiến (dùng + về)
    cols.push({
      k: 'projected',
      h: 'Dự kiến (dùng+về)',
      w: 120,
      num: true,
      tip: () => 'Có thể dùng + Đang về',
      get: (m) => {
        const physical = m.physical_available ?? m.available
        const reserved = m.reserved_base ?? 0
        const avail = Math.max(0, physical - reserved)
        const incoming = m.incoming_qty ?? m.incoming_base ?? 0
        return Math.round((avail + incoming) * 10000) / 10000
      },
    })

    // 12. If in stocktake count mode: Tồn đếm thực tế & Chênh lệch
    if (mode === 'count') {
      cols.push({
        k: 'counted_qty',
        h: 'Tồn đếm thực tế',
        w: 110,
        num: true,
        edit: (m) => !m.is_free_stock,
        get: (m) => (countedQuantities[m.material_id] !== undefined ? countedQuantities[m.material_id] : ''),
        set: (m, val) => {
          const clean = String(val).trim()
          if (clean === '') {
            setCountedQuantities((prev) => {
              const next = { ...prev }
              delete next[m.material_id]
              return next
            })
            return
          }
          const n = Number(clean.replace(',', '.'))
          if (!Number.isFinite(n) || n < 0) {
            return 'Tồn đếm phải là số không âm'
          }
          setCountedQuantities((prev) => ({ ...prev, [m.material_id]: n }))
        },
      })

      cols.push({
        k: 'diff_qty',
        h: 'Chênh lệch',
        w: 90,
        num: true,
        get: (m) => {
          const counted = countedQuantities[m.material_id]
          if (counted === undefined) return ''
          const current = m.physical_available ?? m.available
          return Math.round((counted - current) * 10000) / 10000
        },
        cls: (m) => {
          const counted = countedQuantities[m.material_id]
          if (counted === undefined) return ''
          const current = m.physical_available ?? m.available
          const diff = counted - current
          return diff < 0 ? 'text-red-700 font-bold' : diff > 0 ? 'text-emerald-700 font-bold' : ''
        },
      })
    }

    // 13. Ghi chú
    cols.push({
      k: 'notes',
      h: 'Ghi chú',
      w: 220,
      edit: () => mode === 'edit',
      get: (m) => m.notes || '',
      set: (m, val) => {
        m.notes = String(val).trim()
      },
    })

    return cols
  }, [selectedGroup, mode, countedQuantities, getHealth])

  // Total summary row for grid
  const totalSummary = useCallback(
    (rows: MaterialStockOverviewItem[]) => {
      const materialRows = rows.filter((r) => !r.is_free_stock)
      const totalPhysical = materialRows.reduce((acc, r) => acc + (r.physical_available ?? r.available), 0)
      const totalReserved = materialRows.reduce((acc, r) => acc + (r.reserved_base ?? 0), 0)
      const totalAvail = materialRows.reduce((acc, r) => {
        const phys = r.physical_available ?? r.available
        const res = r.reserved_base ?? 0
        return acc + Math.max(0, phys - res)
      }, 0)
      const totalIncoming = materialRows.reduce((acc, r) => acc + (r.incoming_qty ?? r.incoming_base ?? 0), 0)

      return {
        name: `${rows.length} mặt hàng`,
        physical_available: totalPhysical,
        reserved_base: totalReserved,
        available_to_reserve: totalAvail,
        incoming_qty: totalIncoming,
      }
    },
    [],
  )

  // Export to CSV
  const handleExportCsv = () => {
    const headers = ['STT', ...gridColumns.map((c) => c.h || '')]
    const rows = filteredRows.map((r, i) => [i + 1, ...gridColumns.map((c) => c.get ? c.get(r) : '')])
    exportToCsv(`ton-kho-${selectedGroup}`, headers, rows)
  }

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 bg-white ${mode ? 'bg-[#fff2b8]' : ''}`}
    >
      <div className="px-3 pt-2">
        <Breadcrumb items={[{ label: 'Kho', to: '/warehouse/stock' }, { label: 'Tồn kho' }]} />
        <PageHeader title="Tồn kho" subtitle="Theo dõi và điều chỉnh tồn kho vật tư" />
      </div>
      {/* Tabs: Tất cả | các nhóm vật tư | Hàng tự do (một hàng duy nhất, không trùng chức năng) */}
      <div className="hidden md:flex gap-0 px-2.5 pt-1.5 bg-[#fafafa] border-b border-[#ddd] flex-wrap select-none">
        <button
          type="button"
          onClick={() => { setStockKind('all'); setSelectedGroup('all') }}
          className={`px-3 py-1.5 border border-[#bbb] border-b-0 -mr-px text-[12.5px] cursor-pointer transition-colors ${
            stockKind === 'all' && selectedGroup === 'all' ? 'bg-white font-bold border-[#999]' : 'bg-[#e9e9e9] hover:bg-[#dedede]'
          }`}
        >
          Tất cả <span className="text-[#666] text-xs ml-1">({groupCounts.all})</span>
        </button>

        {MATERIAL_GROUPS.map((g) => (
          <button
            key={g.code}
            type="button"
            onClick={() => { setStockKind('material'); setSelectedGroup(g.code) }}
            className={`px-3 py-1.5 border border-[#bbb] border-b-0 -mr-px text-[12.5px] cursor-pointer transition-colors ${
              stockKind !== 'free' && selectedGroup === g.code ? 'bg-white font-bold border-[#999]' : 'bg-[#e9e9e9] hover:bg-[#dedede]'
            }`}
          >
            {g.name} <span className="text-[#666] text-xs ml-1">({groupCounts[g.code] || 0})</span>
          </button>
        ))}

        {groupCounts.unassigned > 0 && (
          <button
            type="button"
            onClick={() => { setStockKind('material'); setSelectedGroup('unassigned') }}
            className={`px-3 py-1.5 border border-[#bbb] border-b-0 -mr-px text-[12.5px] cursor-pointer transition-colors ${
              stockKind !== 'free' && selectedGroup === 'unassigned' ? 'bg-white font-bold border-[#999]' : 'bg-[#e9e9e9] hover:bg-[#dedede]'
            }`}
          >
            Chưa phân nhóm <span className="text-[#666] text-xs ml-1">({groupCounts.unassigned})</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => { setStockKind('free'); setSelectedGroup('all') }}
          className={`px-3 py-1.5 border border-[#bbb] border-b-0 -mr-px text-[12.5px] cursor-pointer transition-colors ${
            stockKind === 'free' ? 'bg-white font-bold border-[#999]' : 'bg-[#e9e9e9] hover:bg-[#dedede]'
          }`}
        >
          Hàng tự do <span className="text-[#666] text-xs ml-1">({groupCounts.free})</span>
        </button>
      </div>

      {/* Mobile Toolbar (< 768px) */}
      <div className="flex md:hidden flex-col gap-2 p-2 bg-[#fafafa] border-b border-[#ddd]">
        {/* Search input full width */}
        <div className="flex items-center gap-1.5">
          <input
            type="search"
            placeholder="Tìm mã, tên vật tư..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 border border-slate-300 rounded px-2.5 py-1.5 text-xs bg-white outline-none focus:border-blue-500"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="px-2 py-1.5 text-xs bg-slate-200 hover:bg-slate-300 rounded text-slate-700"
            >
              ✕
            </button>
          )}
        </div>

        {/* Filter dropdowns row */}
        <div className="flex items-center gap-1.5">
          <select
            value={selectedGroup}
            onChange={(e) => {
              const val = e.target.value
              if (val === 'free') {
                setStockKind('free')
                setSelectedGroup('all')
              } else {
                setStockKind(val === 'all' ? 'all' : 'material')
                setSelectedGroup(val)
              }
            }}
            className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-xs bg-white"
          >
            <option value="all">Nhóm: Tất cả ({groupCounts.all})</option>
            {MATERIAL_GROUPS.map((g) => (
              <option key={g.code} value={g.code}>
                {g.name} ({groupCounts[g.code] || 0})
              </option>
            ))}
            {groupCounts.unassigned > 0 && (
              <option value="unassigned">Chưa phân nhóm ({groupCounts.unassigned})</option>
            )}
            <option value="free">Hàng tự do ({groupCounts.free})</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-xs bg-white"
          >
            <option value="">Tình trạng: Tất cả</option>
            <option value="below">Dưới ngưỡng ({summaryCounts.below})</option>
            <option value="out">Hết khả dụng ({summaryCounts.out})</option>
            <option value="nothr">Không theo dõi</option>
          </select>
        </div>

        {/* Stats and quick actions strip */}
        <div className="flex items-center justify-between gap-1 text-[11px] text-slate-600 pt-0.5">
          <div>
            Hiển thị <b>{filteredRows.length}</b> VT
            {summaryCounts.below > 0 && (
              <span className="ml-1 text-red-700 font-bold">({summaryCounts.below} chạm ngưỡng)</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                type="button"
                onClick={() => setCreateMaterialModalOpen(true)}
                className="rounded bg-blue-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-2xs hover:bg-blue-700 active:bg-blue-800"
              >
                ＋ Tạo VT
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => setIssueOpen(true)}
                className="rounded bg-amber-700 px-2.5 py-1 text-[11px] font-semibold text-white shadow-2xs hover:bg-amber-800 active:bg-amber-900"
              >
                ⇱ Xuất kho
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Toolbar */}
      <div
        className={`hidden md:flex items-center gap-2 flex-wrap px-2.5 py-1.5 border-b border-[#ddd] ${
          mode ? 'bg-[#fff2b8]' : 'bg-[#fafafa]'
        }`}
      >
        <input
          type="search"
          placeholder="Tìm trong bảng…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border border-[#999] px-2 py-1 text-[12px] bg-white rounded-[2px] w-[200px] outline-none"
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-[#999] px-2 py-1 text-[12px] bg-white rounded-[2px] outline-none"
        >
          <option value="">Tình trạng: Tất cả</option>
          <option value="below">Dưới ngưỡng</option>
          <option value="out">Hết khả dụng</option>
          <option value="nothr">Không theo dõi</option>
        </select>

        <div className="flex-1" />

        {/* Status stats */}
        {!mode && (
          <div className="text-[12px] text-slate-600 mr-2">
            <span className="text-amber-700 font-bold">{summaryCounts.below}</span> dưới ngưỡng ·{' '}
            <span className="text-red-700 font-bold">{summaryCounts.out}</span> hết khả dụng ·{' '}
            {summaryCounts.untracked} không theo dõi
          </div>
        )}

        {mode && (
          <div className="text-[12px] font-bold text-red-700 mr-2">
            {mode === 'count' ? 'ĐANG KIỂM KÊ KHO' : 'ĐANG SỬA'}
          </div>
        )}

        {/* Xem danh sách lưu trữ */}
        <button
          type="button"
          onClick={() => { setShowArchived((v) => !v); setStockKind('all'); setSelectedGroup('all') }}
          className={`border px-2.5 py-1 rounded-[2px] text-[12px] ${showArchived ? 'border-[#555] bg-[#555] text-white' : 'border-[#999] bg-white hover:bg-slate-50'}`}
          title="Vật tư lưu trữ bị ẩn khỏi Tồn kho, khôi phục được; tự xóa sau 30 ngày nếu không phát sinh gì"
        >
          {showArchived ? '← Về Tồn kho' : 'Lưu trữ'}
        </button>
        {archiveError ? <span className="text-xs text-red-700">{archiveError}</span> : null}

        {/* Buttons */}
        {!mode ? (
          <>
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => setIssueOpen(true)}
              className="border border-[#b45309] bg-[#b45309] text-white hover:bg-[#92400e] disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded-[2px] text-[12px] font-medium"
              title="Lập phiếu xuất kho: chọn vật tư và số lượng lấy ra khỏi kho"
            >
              ⇱ Xuất vật tư
            </button>
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => setCreateMaterialModalOpen(true)}
              className="border border-[#0b4fb3] bg-[#0b4fb3] text-white hover:bg-[#093c88] disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded-[2px] text-[12px] font-medium"
            >
              ＋ Tạo vật tư
            </button>
            <button
              type="button"
              disabled={!canEdit}
              onClick={handleStartEdit}
              className="border border-[#999] bg-white hover:bg-[#eee] disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded-[2px] text-[12px]"
            >
              ✎ Sửa
            </button>
            <button
              type="button"
              disabled={!canEdit}
              onClick={handleStartCount}
              className="border border-[#999] bg-white hover:bg-[#eee] disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded-[2px] text-[12px]"
            >
              ▤ Kiểm kê
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={mode === 'edit' ? handleConfirmEdit : handlePrepareStocktake}
              className="border border-[#222] bg-[#222] text-white hover:bg-black px-3 py-1 rounded-[2px] text-[12px] font-bold"
            >
              ✓ Xác nhận
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={handleCancelMode}
              className="border border-[#999] bg-white hover:bg-[#eee] px-2.5 py-1 rounded-[2px] text-[12px]"
            >
              ✕ Hủy bỏ
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => {
            setSearchQuery('')
            setStatusFilter('')
            setClearFilterCounter((c) => c + 1)
          }}
          className="border border-[#999] bg-white hover:bg-[#eee] px-2.5 py-1 rounded-[2px] text-[12px]"
        >
          ✕ Bỏ lọc
        </button>

        <button
          type="button"
          onClick={() => setResetWidthsCounter((c) => c + 1)}
          title="Đặt lại độ rộng các cột về mặc định"
          className="border border-[#999] bg-white hover:bg-[#eee] px-2.5 py-1 rounded-[2px] text-[12px]"
        >
          ↔ Đặt lại độ rộng
        </button>

        <button
          type="button"
          onClick={handleExportCsv}
          className="border border-[#999] bg-white hover:bg-[#eee] px-2.5 py-1 rounded-[2px] text-[12px]"
        >
          ⬇ Excel
        </button>
      </div>

      {/* Grid Content */}
      <div className="flex flex-col flex-1 min-h-0 relative overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full text-slate-500 text-sm">
            Đang tải dữ liệu tồn kho…
          </div>
        ) : error ? (
          <div className="p-4 text-red-600 bg-red-50 text-sm">{error}</div>
        ) : (
          <>
            {/* Desktop View: Original ExcelGrid */}
            <div className="hidden md:flex flex-col flex-1 min-h-0">
              <ExcelGrid<MaterialStockOverviewItem>
                id={`warehouse-stock-${selectedGroup}`}
            data={filteredRows}
            columns={gridColumns}
            freeze={3}
            isEditing={Boolean(mode)}
            total={totalSummary}
            rowCls={(row) => {
              const avail = (row.physical_available ?? row.available) - (row.reserved_base ?? 0)
              const threshold = row.threshold ?? Math.max(row.min_qty ?? 0, row.safety_stock ?? 0)
              if (threshold > 0 && avail <= threshold) {
                return '[&>td:not(.stt)]:text-red-700 font-semibold'
              }
              return undefined
            }}
            clearFilterTrigger={clearFilterCounter}
            resetWidthsTrigger={resetWidthsCounter}
            onCellClick={(row, col) => {
              if (col.k === 'detail_name') {
                setSelectedMaterialForPanel(row.material_id)
              } else if (col.k === 'reserved_base') {
                void handleOpenHoldModal(row)
              }
            }}
            onRowContextMenu={(row, e) => {
              if (row.is_free_stock) {
                e.preventDefault()
                setContextMenu({ row, x: e.clientX, y: e.clientY })
                return
              }
              if (!canEdit) return
              e.preventDefault()
              setContextMenu({ row, x: e.clientX, y: e.clientY })
            }}
              />
            </div>

            {/* Mobile View: Touch-Friendly Card List */}
            <div className="flex md:hidden flex-col flex-1 min-h-0">
              <StockMobileCardList
                items={filteredRows}
                onOpenHoldModal={handleOpenHoldModal}
                onOpenProperties={(id) => setSelectedMaterialForPanel(id)}
                isBelowThreshold={(m) => {
                  const avail = (m.physical_available ?? m.available) - (m.reserved_base ?? 0)
                  const threshold = m.threshold ?? Math.max(m.min_qty ?? 0, m.safety_stock ?? 0)
                  return threshold > 0 && avail <= threshold
                }}
                stockDisplayName={stockDisplayName}
                canEdit={canEdit}
                mode={mode}
                countedQuantities={countedQuantities}
                onCountChange={(id, val) => {
                  const clean = String(val).trim()
                  if (clean === '') {
                    setCountedQuantities((prev) => {
                      const next = { ...prev }
                      delete next[id]
                      return next
                    })
                    return
                  }
                  const n = Number(clean.replace(',', '.'))
                  if (Number.isFinite(n) && n >= 0) {
                    setCountedQuantities((prev) => ({ ...prev, [id]: n }))
                  }
                }}
              />
            </div>
          </>
        )}
      </div>

      {contextMenu && !contextMenu.row.is_free_stock ? (
        <div className="fixed z-[500] min-w-[210px] border border-[#999] bg-white shadow-lg text-xs" style={{ left: contextMenu.x, top: contextMenu.y }} onMouseLeave={() => setContextMenu(null)}>
          {showArchived ? (
            <button type="button" className="block w-full px-3 py-2 text-left hover:bg-slate-100" onClick={() => { void handleRestoreMaterial(contextMenu.row); setContextMenu(null) }}>Khôi phục vật tư</button>
          ) : (
            <button type="button" disabled={((contextMenu.row.physical_available ?? contextMenu.row.available) !== 0)} className="block w-full px-3 py-2 text-left hover:bg-slate-100 disabled:text-slate-400 disabled:hover:bg-white" onClick={() => { void handleArchiveMaterial(contextMenu.row); setContextMenu(null) }}>{((contextMenu.row.physical_available ?? contextMenu.row.available) !== 0) ? 'Không lưu trữ được — còn tồn' : 'Lưu trữ vật tư (tự xóa sau 30 ngày)'}</button>
          )}
          <button type="button" disabled={((contextMenu.row.physical_available ?? contextMenu.row.available) !== 0)} className="block w-full px-3 py-2 text-left hover:bg-slate-100 disabled:text-slate-400 disabled:hover:bg-white" onClick={() => { setDeleteMaterial(contextMenu.row); setContextMenu(null) }}>{((contextMenu.row.physical_available ?? contextMenu.row.available) !== 0) ? `Không xóa được — còn tồn ${contextMenu.row.physical_available ?? contextMenu.row.available}` : 'Xóa vật tư…'}</button>
        </div>
      ) : contextMenu && contextMenu.row.free_stock_id ? (
        <div className="fixed z-[500] min-w-[210px] border border-[#999] bg-white shadow-lg text-xs" style={{ left: contextMenu.x, top: contextMenu.y }} onMouseLeave={() => setContextMenu(null)}>
          <button type="button" className="block w-full px-3 py-2 text-left hover:bg-slate-100" onClick={() => { setFreeConvert({ id: contextMenu.row.free_stock_id!, po_id: '', po_free_line_id: '', name: contextMenu.row.name, uom: contextMenu.row.uom_default || '', details: contextMenu.row.free_stock_details as any, unit_price: null, qty_on_hand: contextMenu.row.physical_available || 0, qty_received_total: contextMenu.row.physical_available || 0, created_at: '' }); setContextMenu(null) }}>Chuyển sang vật tư</button>
          <button type="button" className="block w-full px-3 py-2 text-left hover:bg-slate-100" onClick={async () => { setFreeDetail(await getFreeStockItem(contextMenu.row.free_stock_id!)); setContextMenu(null) }}>Xem lịch sử biến động</button>
          <button type="button" disabled={(contextMenu.row.physical_available ?? 0) !== 0} className="block w-full px-3 py-2 text-left hover:bg-slate-100 disabled:text-slate-400 disabled:hover:bg-white" onClick={() => { setDeleteFree(contextMenu.row); setContextMenu(null) }}>{(contextMenu.row.physical_available ?? 0) !== 0 ? `Không xóa được — còn tồn ${contextMenu.row.physical_available}` : 'Xóa hàng tự do…'}</button>
        </div>
      ) : null}
      <CreateIssueSlipModal
        isOpen={issueOpen}
        onClose={() => setIssueOpen(false)}
        onSuccess={() => { setIssueOpen(false); void loadStockData() }}
      />
      {deleteFree && (
        <div className="fixed inset-0 bg-black/40 z-[600] flex items-center justify-center p-3" role="dialog" aria-modal="true">
          <div className="bg-white border border-[#777] w-[min(460px,96%)] shadow-[0_8px_28px_rgba(0,0,0,0.35)] p-4">
            <h2 className="font-bold text-sm">Xóa hàng tự do {deleteFree.name}?</h2>
            <p className="mt-2 text-xs text-slate-700">Chỉ xóa được khi tồn bằng 0. Hàng sẽ bị ẩn khỏi danh sách, lịch sử biến động vẫn giữ.</p>
            {deleteError && <div className="mt-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">{deleteError}</div>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="border border-[#999] bg-white px-3 py-1 rounded-[2px] text-xs" onClick={() => { setDeleteFree(null); setDeleteError(null) }}>Hủy</button>
              <button type="button" disabled={deleteSubmitting} className="border border-red-700 bg-red-700 text-white px-3 py-1 rounded-[2px] text-xs disabled:opacity-50" onClick={() => void handleDeleteFree()}>Xóa</button>
            </div>
          </div>
        </div>
      )}
      {deleteMaterial && (
        <div className="fixed inset-0 bg-black/40 z-[600] flex items-center justify-center p-3" role="dialog" aria-modal="true">
          <div className="bg-white border border-[#777] w-[min(460px,96%)] shadow-[0_8px_28px_rgba(0,0,0,0.35)] p-4">
            <h2 className="font-bold text-sm">Xóa vật tư {deleteMaterial.name}?</h2>
            <p className="mt-2 text-xs text-slate-700">Hành động này không hoàn tác được và vật tư sẽ bị ẩn khỏi danh sách.</p>
            {deleteError && <div className="mt-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">{deleteError}</div>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="border border-[#999] bg-white px-3 py-1 rounded-[2px] text-xs" onClick={() => { setDeleteMaterial(null); setDeleteError(null) }}>Hủy</button>
              <button type="button" disabled={deleteSubmitting} className="border border-red-700 bg-red-700 text-white px-3 py-1 rounded-[2px] text-xs disabled:opacity-50" onClick={() => void handleDeleteMaterial()}>Xóa</button>
            </div>
          </div>
        </div>
      )}
      <FreeStockDetailModal itemId={freeDetail?.id ?? null} initialDetail={freeDetail} onClose={() => setFreeDetail(null)} />
      <FreeConversionModal item={freeConvert} isOpen={Boolean(freeConvert)} onClose={() => setFreeConvert(null)} onSuccess={() => { setFreeConvert(null); void loadStockData() }} />

      {/* Modal "Đang giữ cho việc gì" & Giữ hàng nhanh */}
      {activeHoldMaterial && (
        <div className="fixed inset-0 bg-black/40 z-[200] flex items-center justify-center p-2 sm:p-3">
          <div className="bg-white border border-[#777] w-[min(1250px,98%)] max-h-[94vh] flex flex-col shadow-[0_8px_28px_rgba(0,0,0,0.35)] rounded-sm">
            <div className="p-2.5 border-b border-[#bbb] bg-[#f3f3f3] font-bold flex items-center gap-2 text-sm">
              <span className="truncate">Đang giữ cho việc gì — {activeHoldMaterial.name}</span>
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => setActiveHoldMaterial(null)}
                className="border border-[#999] bg-white hover:bg-[#eee] px-2 py-0.5 rounded-[2px] text-xs font-normal"
              >
                ✕
              </button>
            </div>

            <div className="p-3 overflow-auto flex flex-col gap-3 flex-1 min-h-0 text-[12.5px]">
              {/* Summary Stats Strip */}
              <div className="grid grid-cols-3 gap-2 p-2 bg-[#fafafa] border border-[#ddd] text-xs">
                <div>
                  <span className="text-slate-500">Tồn thực tế: </span>
                  <b>
                    {(activeHoldMaterial.physical_available ?? activeHoldMaterial.available).toLocaleString('vi-VN')}{' '}
                    {activeHoldMaterial.uom_default}
                  </b>
                </div>
                <div>
                  <span className="text-slate-500">Đã giữ: </span>
                  <b className="text-blue-800">{(activeHoldMaterial.reserved_base ?? 0).toLocaleString('vi-VN')}</b>
                </div>
                <div>
                  <span className="text-slate-500">Có thể dùng: </span>
                  <b className="text-emerald-800">
                    {Math.max(
                      0,
                      (activeHoldMaterial.physical_available ?? activeHoldMaterial.available) -
                        (activeHoldMaterial.reserved_base ?? 0),
                    ).toLocaleString('vi-VN')}
                  </b>
                </div>
              </div>

              {/* Form Giữ hàng nhanh (Mobile & Desktop) */}
              {canEdit && (
                <div className="border border-blue-200 bg-blue-50/40 rounded p-2.5">
                  <div className="font-semibold text-xs text-blue-900 mb-1.5 flex items-center justify-between">
                    <span>⚡ Giữ hàng nhanh</span>
                    <span className="text-[11px] font-normal text-slate-500">
                      Tối đa:{' '}
                      {Math.max(
                        0,
                        (activeHoldMaterial.physical_available ?? activeHoldMaterial.available) -
                          (activeHoldMaterial.reserved_base ?? 0),
                      ).toLocaleString('vi-VN')}{' '}
                      {activeHoldMaterial.uom_default}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                    <input
                      type="number"
                      step="any"
                      min="0.001"
                      placeholder={`Số lượng giữ (${activeHoldMaterial.uom_default || 'ĐVT'})`}
                      value={quickHoldQty}
                      onChange={(e) => setQuickHoldQty(e.target.value)}
                      className="border border-slate-300 rounded px-2.5 py-1.5 text-xs bg-white flex-1 outline-none focus:border-blue-500 font-mono"
                    />
                    <input
                      type="text"
                      placeholder="Ghi chú / lý do giữ (tùy chọn)"
                      value={quickHoldNote}
                      onChange={(e) => setQuickHoldNote(e.target.value)}
                      className="border border-slate-300 rounded px-2.5 py-1.5 text-xs bg-white flex-1 sm:flex-2 outline-none focus:border-blue-500"
                    />
                    <button
                      type="button"
                      disabled={quickHoldSubmitting || !quickHoldQty}
                      onClick={handleCreateQuickHold}
                      className="border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-1.5 rounded text-xs font-semibold shrink-0 transition"
                    >
                      {quickHoldSubmitting ? 'Đang lưu…' : '＋ Giữ hàng'}
                    </button>
                  </div>
                </div>
              )}

              {/* Danh sách giữ chỗ */}
              <div className="font-semibold text-xs text-slate-800">
                Danh sách giữ chỗ đang hoạt động ({activeHoldReservations.length})
              </div>

              {holdModalLoading ? (
                <div className="py-8 text-center text-slate-500">Đang tải chi tiết giữ hàng…</div>
              ) : activeHoldReservations.length === 0 ? (
                <div className="py-6 text-center text-slate-500 bg-slate-50 border border-slate-200 rounded">
                  Không có giữ chỗ nào đang hoạt động cho vật tư này.
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-auto border border-[#bbb] max-h-[45vh]">
                    <table className="w-full border-collapse text-[12px]">
                      <thead>
                        <tr className="bg-[#efefef] font-bold text-center border-b border-[#bbb]">
                          <th className="p-1 border-r border-[#bbb] w-10">STT</th>
                          <th className="p-1 border-r border-[#bbb] w-28">Lệnh SX</th>
                          <th className="p-1 border-r border-[#bbb] w-28">Đơn hàng (SO)</th>
                          <th className="p-1 border-r border-[#bbb] w-40">Khách hàng</th>
                          <th className="p-1 border-r border-[#bbb] w-36">K/H-PO</th>
                          <th className="p-1 border-r border-[#bbb] w-28">Mã hàng</th>
                          <th className="p-1 border-r border-[#bbb] w-20 text-right">SL giữ</th>
                          <th className="p-1 border-r border-[#bbb] w-20 text-right">Đã xuất</th>
                          <th className="p-1 border-r border-[#bbb] w-20 text-right">Còn giữ</th>
                          <th className="p-1 border-r border-[#bbb] w-28">Nguồn giữ</th>
                          <th className="p-1 border-r border-[#bbb] w-24">Người giữ</th>
                          <th className="p-1 w-20">Thao tác</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeHoldReservations.map((res, idx) => (
                          <tr key={res.id} className="border-b border-[#ccc] hover:bg-[#f8f9fa]">
                            <td className="p-1 text-center font-mono border-r border-[#ccc]">{idx + 1}</td>
                            <td className="p-1 text-center font-bold border-r border-[#ccc]">
                              {res.plan_line_code || res.plan_line_id || '—'}
                            </td>
                            <td className="p-1 border-r border-[#ccc]">{res.order_code || '—'}</td>
                            <td className="p-1 border-r border-[#ccc]">{res.customer_name || '—'}</td>
                            <td className="p-1 border-r border-[#ccc]">{res.customer_po || '—'}</td>
                            <td className="p-1 border-r border-[#ccc]">{res.product_code || '—'}</td>
                            <td className="p-1 text-right font-mono border-r border-[#ccc]">
                              {res.qty_reserved_base.toLocaleString('vi-VN')}
                            </td>
                            <td className="p-1 text-right font-mono border-r border-[#ccc]">
                              {res.qty_consumed_base.toLocaleString('vi-VN')}
                            </td>
                            <td className="p-1 text-right font-mono font-bold border-r border-[#ccc]">
                              {res.remaining.toLocaleString('vi-VN')}
                            </td>
                            <td className="p-1 border-r border-[#ccc]">{res.demand_type || res.batch_title || '—'}</td>
                            <td className="p-1 border-r border-[#ccc]">{res.created_by}</td>
                            <td className="p-1 text-center">
                              {canEdit && res.status === 'ACTIVE' && res.remaining > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => handleReleaseReservation(res)}
                                  className="text-red-700 underline hover:text-red-900 text-xs font-semibold"
                                >
                                  Bỏ giữ
                                </button>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Reservation Cards */}
                  <div className="flex md:hidden flex-col gap-2 overflow-y-auto max-h-[45vh]">
                    {activeHoldReservations.map((res, idx) => (
                      <div key={res.id} className="border border-slate-200 rounded p-2.5 bg-white shadow-2xs text-xs">
                        <div className="flex items-center justify-between font-semibold border-b border-slate-100 pb-1.5 mb-1.5">
                          <span className="text-slate-800">
                            #{idx + 1} · {res.order_code || res.plan_line_code || 'Giữ thủ công'}
                          </span>
                          <span className="font-bold text-blue-800 font-mono">
                            {res.remaining.toLocaleString('vi-VN')} {activeHoldMaterial.uom_default}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-[11px] text-slate-600 mb-2">
                          <div>Khách: <b className="text-slate-800">{res.customer_name || '—'}</b></div>
                          <div>Mã hàng: <b className="text-slate-800">{res.product_code || '—'}</b></div>
                          <div>Người giữ: <span className="text-slate-700">{res.created_by}</span></div>
                          <div>Ghi chú: <span className="text-slate-700">{res.note || res.demand_type || '—'}</span></div>
                        </div>
                        {canEdit && res.status === 'ACTIVE' && res.remaining > 0 && (
                          <div className="border-t border-slate-100 pt-1.5 flex justify-end">
                            <button
                              type="button"
                              onClick={() => handleReleaseReservation(res)}
                              className="text-red-700 border border-red-200 bg-red-50 hover:bg-red-100 px-3 py-1 rounded text-xs font-semibold"
                            >
                              Bỏ giữ
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="p-2.5 border-t border-[#bbb] bg-[#f3f3f3] flex justify-end">
              <button
                type="button"
                onClick={() => setActiveHoldMaterial(null)}
                className="border border-[#999] bg-white hover:bg-[#eee] px-4 py-1 rounded-[2px] text-xs"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Xác nhận kiểm kê */}
      {showStocktakeModal && (
        <div className="fixed inset-0 bg-black/40 z-[200] flex items-center justify-center p-3">
          <div className="bg-white border border-[#777] w-[min(900px,98%)] max-h-[90vh] flex flex-col shadow-[0_8px_28px_rgba(0,0,0,0.35)]">
            <div className="p-2.5 border-b border-[#bbb] bg-[#f3f3f3] font-bold flex items-center text-sm">
              <span>Xác nhận kiểm kê — tạo phiếu điều chỉnh tồn</span>
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => setShowStocktakeModal(false)}
                className="border border-[#999] bg-white hover:bg-[#eee] px-2 py-0.5 rounded-[2px] text-xs font-normal"
              >
                ✕
              </button>
            </div>

            <div className="p-3 overflow-auto flex flex-col gap-3 flex-1 min-h-0 text-[12.5px]">
              <div className="p-2 bg-[#fafafa] border border-[#ddd] text-xs text-slate-700">
                Mỗi dòng ghi một biến động <b>Điều chỉnh</b> vào sổ cái kho với mã chứng từ ADJ chung.
              </div>

              <div className="border border-[#bbb] max-h-[45vh] overflow-auto">
                <table className="w-full border-collapse text-[12px]">
                  <thead>
                    <tr className="bg-[#efefef] font-bold text-center border-b border-[#bbb]">
                      <th className="p-1 border-r border-[#bbb] w-10">STT</th>
                      <th className="p-1 border-r border-[#bbb]">Tên vật tư</th>
                      <th className="p-1 border-r border-[#bbb] w-16">ĐVT</th>
                      <th className="p-1 border-r border-[#bbb] w-24 text-right">Tồn hệ thống</th>
                      <th className="p-1 border-r border-[#bbb] w-24 text-right">Tồn đếm</th>
                      <th className="p-1 border-r border-[#bbb] w-24 text-right">Chênh lệch</th>
                      <th className="p-1 w-48 text-left">Cảnh báo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(countedQuantities)
                      .map((id) => {
                        const m = materials.find((mat) => mat.material_id === id)
                        if (!m) return null
                        const current = m.physical_available ?? m.available
                        const counted = countedQuantities[id]
                        const diff = Math.round((counted - current) * 10000) / 10000
                        const isBelowReserved = counted < (m.reserved_base ?? 0)
                        return { m, current, counted, diff, isBelowReserved }
                      })
                      .filter(Boolean)
                      .map((row, idx) => (
                        <tr key={row!.m.material_id} className="border-b border-[#ccc] hover:bg-[#f8f9fa]">
                          <td className="p-1 text-center font-mono border-r border-[#ccc]">{idx + 1}</td>
                          <td className="p-1 border-r border-[#ccc] font-medium">{row!.m.name}</td>
                          <td className="p-1 text-center border-r border-[#ccc]">{row!.m.uom_default}</td>
                          <td className="p-1 text-right font-mono border-r border-[#ccc]">
                            {row!.current.toLocaleString('vi-VN')}
                          </td>
                          <td className="p-1 text-right font-mono border-r border-[#ccc] font-bold">
                            {row!.counted.toLocaleString('vi-VN')}
                          </td>
                          <td
                            className={`p-1 text-right font-mono border-r border-[#ccc] font-bold ${
                              row!.diff < 0 ? 'text-red-700' : 'text-emerald-700'
                            }`}
                          >
                            {row!.diff > 0 ? `+${row!.diff.toLocaleString('vi-VN')}` : row!.diff.toLocaleString('vi-VN')}
                          </td>
                          <td className="p-1 text-left text-xs">
                            {row!.isBelowReserved ? (
                              <span className="text-red-700 font-bold">
                                Thấp hơn số đang giữ ({row!.m.reserved_base})
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Lý do điều chỉnh kiểm kê *
                </label>
                <textarea
                  rows={2}
                  className="w-full border border-[#999] p-2 text-xs rounded-[2px] outline-none focus:border-black"
                  placeholder="VD: Kiểm kê định kỳ cuối tháng, hỏng trong quá trình vận chuyển, thất thoát…"
                  value={stocktakeReason}
                  onChange={(e) => setStocktakeReason(e.target.value)}
                />
              </div>
            </div>

            <div className="p-2.5 border-t border-[#bbb] bg-[#f3f3f3] flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowStocktakeModal(false)}
                className="border border-[#999] bg-white hover:bg-[#eee] px-3 py-1 rounded-[2px] text-xs"
              >
                Quay lại
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => handleSubmitStocktake(false)}
                className="border border-[#222] bg-[#222] text-white hover:bg-black px-4 py-1 rounded-[2px] text-xs font-bold"
              >
                {isSubmitting ? 'Đang tạo phiếu…' : 'Tạo phiếu điều chỉnh'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Material Properties Panel Drawer */}
      <MaterialPropertiesPanel
        isOpen={Boolean(selectedMaterialForPanel)}
        materialId={selectedMaterialForPanel}
        onClose={() => setSelectedMaterialForPanel(null)}
        onSaved={() => {
          void loadStockData()
        }}
      />

      {/* Quick Material Creation Modal */}
      <CreateMaterialModal
        isOpen={createMaterialModalOpen}
        onClose={() => setCreateMaterialModalOpen(false)}
        onSuccess={() => {
          setCreateMaterialModalOpen(false)
          void loadStockData()
        }}
      />
    </div>
  )
}

